<template>
  <el-alert
      :title="`近期有${title}缓存，是否替换为缓存的内容？`"
      type="warning"
      close-text="替换"
      show-icon
      @close="useCache"
      v-if="props.cacheId === itemDescStore().itemDesc.cacheId && itemDescStore().itemDesc.cacheId !== '0' && content !== itemDescStore().itemDesc.content"
  />
  <div id="YTEditor_container" v-loading="loading">
    <QuillEditor
        ref="quillEditor"
        v-model:content="content"
        theme="snow"
        contentType="html"
        :options="editorOptions"
        @blur="onEditorBlur"
        @focus="onEditorFocus"
        @ready="onEditorReady"
    />
    <div v-show="props.isShowUpload" class="editorCount" style="position: absolute;bottom: 5px;right: 25px">
      <v-icon icon="mdi-panorama-variant"></v-icon>
      {{uploadedImages}} / {{props.maxPic}}
    </div>
    <div :class="contentLength <= MAX_CHAR_COUNT ? 'editorCount' : 'editorCountErr'" :style="`position: absolute;bottom: 5px;right: ${ (props.isShowUpload ? 60 : 0) + 25}px`">
      <v-icon icon="mdi-text-shadow"></v-icon>
      {{contentLength}} / {{MAX_CHAR_COUNT}}
    </div>
  </div>
</template>

<script setup>
import {onMounted, onUnmounted, reactive, ref, watch} from 'vue'
import {addQuillTitle} from "@/assets/js/quill-title.js"
import {uploadImg} from "@/api/filesUpload/filesUpload.js"
import {Quill, QuillEditor} from '@vueup/vue-quill'
import BlotFormatter from 'quill-blot-formatter/dist/BlotFormatter'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import 'quill-emoji/dist/quill-emoji.css';
import 'quill-emoji/dist/quill-emoji.js';
import {defineStore} from "pinia";
import axios from "axios";
import {getSignatureUrl} from "@/api/upload/upload.js";
import {useSnowflakeHook} from "@/hooks/snowflake.js";
const { generateId } = useSnowflakeHook();

// const Size = Quill.import('attributors/style/size');
// Size.whitelist = ['8px', '10px', '12px', '14px', '16px', '18px', '20px', '24px', '32px'];
// Quill.register(Size, true);

Quill.register("modules/blotFormatter", BlotFormatter)

const props = defineProps({
  // 最大文字长度
  maxLength: {
    type: Number,
    default: 1000
  },
  // 最大图片上传数量
  maxPic: {
    type: Number,
    default: 5
  },
  // 单张图片上传大小，单位：MB
  maxPicSize: {
    type: Number,
    default: 5
  },
  // 富文本标题，用于缓存提示的展示
  title: {
    type: String,
    default: "富文本内容"
  },
  // 回显文本
  reviewTxt: {
    type: String,
    default: null
  },
  // 是否开启缓存模式
  isCacheMode: {
    type: Boolean,
    default: false
  },
  // 缓存id，配合缓存模式使用
  cacheId: {
    type: String,
    default: "0"
  },
  // 缓存key，配合缓存模式使用
  cacheKey: {
    type: String,
    default: "default"
  },
  // 阿里oss预上传地址枚举
  aliOssPath: {
    required: true,
    type: String,
  },
  isShowUpload: {
    type: Boolean,
    default: true
  }
})

/*商品富文本缓存信息*/
const itemDescStore = defineStore(
    props.cacheKey,
    () => {
      const itemDesc = ref({
        cacheId: '0',
        content: '',
        ttl: ''
      })
      const setItemDesc = (newItemDesc) => {
        itemDesc.value = newItemDesc
      }
      const removeItemDesc = () => {
        itemDesc.value = {
          cacheId: '0',
          content: '',
          ttl: ''
        }
      }
      return {
        itemDesc,
        setItemDesc,
        removeItemDesc
      }
    },
    {
      persist: true
    }
)

// 加载状态
const loading = ref(false)
// 编辑器内容
const content = ref("")
// 获取 Quill 实例
const quillEditor = ref(null)

// 事件
const emit = defineEmits(["change"])

/*图片上传相关*/
// 图片上传处理逻辑
const imageHandler = async () => {
  // 检查已上传的图片数量
  if (uploadedImages.value >= MAX_IMAGE_COUNT) {
    ElMessage.warning('你已经达到上传图片的最大数量');
    return;
  }
  // 1. 创建文件选择输入框
  const input = document.createElement("input");
  input.setAttribute("type", "file");
  input.setAttribute("accept", "image/*");
  input.click();

  input.onchange = async () => {
    const file = input.files[0];
    if (!file) return;

    // 2. 限制文件类型和大小（5MB）
    const MAX_SIZE = props.maxPicSize * 1024 * 1024; // 5MB
    if (file.size > MAX_SIZE) {
      alert(`图片大小不能超过${props.maxPicSize}MB`);
      return;
    }

    try {
      loading.value = true
      // 3. 调用API上传图片
      const resp = await uploadImage(file);
      const url = resp.fileUrl[0]
      console.log("resp", resp)
      console.log("url", url)
      // 4. 插入图片到编辑器
      const quill = quillEditor.value.getQuill();
      let range = quill.getSelection();

      if (!range) {
        // 如果没有光标选区，主动聚焦并设置一个默认位置
        quill.focus();
        range = quill.getSelection(true); // true = 获取最新光标
        if (!range) {
          range = { index: quill.getLength(), length: 0 }; // 插到末尾
        }
      }

      quill.insertEmbed(range.index, "image", url);
      quill.setSelection(range.index + 1); // 移动光标到图片后
      // 上传成功，图片数量增加
      uploadedImages.value += 1;
      loading.value = false
    } catch (error) {
      console.log(error)
      loading.value = false
      ElMessage.error("图片上传失败，失败原因: " + error);
    }
  };
};

// 上传图片
const uploadImage = async (file) => {
  // 获取预上传地址
  console.log("file", file)
  const urlParam = await getFileUploadUrl(file)
  if (urlParam.code === -1) return ElMessage.error("上传失败")
  const urlObj = new URL(urlParam.signedUrl);
  const fileUrl = urlObj.origin + urlObj.pathname;

  try {
    const response = await axios.put(urlParam.signedUrl, file, {
      headers: { 'Content-Type': 'application/octet-stream' }
    });

    console.log("fileUrl", [fileUrl])
    console.log("file", file)

    if (response.status === 200) {
      return { code: 0, fileUrl: [fileUrl], fileInfo: file };
    } else {
      return { code: -1, fileUrl: null };
    }

  } catch (err) {
    console.error(err);
    return { code: -1, fileUrl: null };
  }
};

// 获取文件预上传地址
const getFileUploadUrl = async (file) => {
  try {
    const id = generateId()
    // 这里和el-upload不同，获取到的就是file.raw文件流，所以直接使用file
    const rawFile = file
    console.log("rawFile", rawFile)
    // 获取文件后缀
    const ext = rawFile.name.includes('.') ? rawFile.name.substring(rawFile.name.lastIndexOf('.')) : '.bin';
    let param = {
      uploadDownloadTypeEnum: "PERMANENT",
      aliOssImgUploadPathEnum: props.aliOssPath,
      fileName: props.aliOssPath + id + ext
    }
    const res = await getSignatureUrl(param)
    console.log("res", res)
    return {code: 0, signedUrl: res}
  } catch (e) {
    console.log(e)
    return {code: -1, signedUrl: null}
  }
}

// 编辑器配置
const editorOptions = reactive({
  modules: {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'],
        ['emoji'] // 添加 Emoji 按钮
        ['blockquote'],
        [{list: 'ordered'}, {list: 'bullet'}],
        [{indent: '-1'}, {indent: '+1'}],
        [{size: ['small', false, 'large', 'huge']}],
        // [{size: Size.whitelist}],
        [{header: [1, 2, 3, 4, 5, 6, false]}],
        [{color: []}, {background: []}], // 颜色选择器
        [{align: []}],
        ['clean'],
        [props.isShowUpload ? 'image' : '']
      ],
      handlers: {
        image: imageHandler // 自定义图片处理
      }
    },
    'emoji-toolbar': true, // 启用 Emoji 工具栏
    'emoji-textarea': true, // 启用 Emoji 文本区域
    'emoji-shortname': true, // 启用 Emoji 短名称
    blotFormatter: {}
  }
})

// 最大字符数限制
const MAX_CHAR_COUNT = props.maxLength;
// 标志变量，避免循环触发事件
let isSettingText = false;
// 限制上传图片数量
const MAX_IMAGE_COUNT = props.maxPic;
// 记录已上传的图片数量
const uploadedImages = ref(0);
// 显示的内容长度
const contentLength = ref(0)
// 文本是否过长
const isTxtTooLong = ref(false)
// 初始化时设置默认字体颜色
onMounted(() => {
  if (quillEditor.value) {
    addQuillTitle();
    const quill = quillEditor.value.getQuill();
    quill.format('color', '#000'); // 设置默认字体颜色为黑色
    quill.format('header', false);

    let timeoutId = null;

    // 监听 text-change 事件
    quill.on('text-change', () => {
      // 如果正在修改文本，直接返回，不执行处理
      if (isSettingText) return;

      // 清除之前的定时器
      if (timeoutId) clearTimeout(timeoutId);

      // 设置新的定时器，防止去除图片时触发过快导致的错误
      timeoutId = setTimeout(async () => {
        // 获取富文本编辑器中的原始HTML
        const htmlContent = quill.root.innerHTML;
        console.log("htmlContent", htmlContent)
        // 移除所有 <br> 标签和 &nbsp;（不间断空格）实体
        const textWithoutHtml = htmlContent
            .replace(/<br\s*\/?>/g, '') // 去掉所有 <br> 标签
            .replace(/<\/?[^>]+(>|$)/g, ''); // 移除所有 HTML 标签
         // 去掉前后空格
        const charCount = textWithoutHtml.length;
        // console.log('charCount', charCount);
        contentLength.value = charCount;
        const imgArr = getImagesUrlArr()
        emit('change', isContentEmpty(content.value) ? "" : content.value, imgArr)
        if (charCount > MAX_CHAR_COUNT) {
          // 设置标志，表示正在修改文本
          isSettingText = true;
          isTxtTooLong.value = true;
          ElMessage.warning('超过限制字数');
          // 重置标志
          isSettingText = false;
        } else {
          isTxtTooLong.value = false;
        }

        // 获取编辑器中的所有图片
        const images = quill.root.querySelectorAll('img');
        // 校验images中是否有base64图片
        let hasBase64 = false;
        images.forEach((image) => {
          if (image.src.includes('data:image')) {
            image.remove();
            hasBase64 = true;
          }
        });
        if (hasBase64) {
          ElMessage.error(`请勿截图添加图片，需要添加图片请点击"图片"上传`);
        }
        uploadedImages.value = images.length; // 更新图片数量
        console.log(`当前图片数量：${uploadedImages.value}`);
      }, 300); // 延迟 300ms 执行
    });

    if (props.isCacheMode) {
      // 查询是否有缓存，有则进行恢复
      if (itemDescStore().itemDesc) {
        // 校验过期时间，缓存过期时间为30分钟，超过则清除缓存
        let currentTime = Date.now();
        if (currentTime - itemDescStore().itemDesc.ttl > 30 * 60 * 1000) {
          console.log('过期时间大于30分钟，清空缓存');
          itemDescStore().removeItemDesc();
          return;
        }
        content.value =
            itemDescStore().itemDesc.cacheId === props.cacheId
                ? itemDescStore().itemDesc.content
                : '';
      }
    }
    // 反显兜底（清洗 Quill 光标占位符）
    if (props.reviewTxt) {
      content.value = cleanReviewContent(props.reviewTxt)
    }
  }
});

// 清洗富文本中的 Quill 光标占位符和零宽字符
const cleanReviewContent = (html) => {
  if (!html) return ""
  const cleaned = html
      .replace(/<span class="ql-cursor">.*?<\/span>/g, '')
      .replace(/﻿/g, '')
      .replace(/&nbsp;/g, ' ')
  const stripped = cleaned
      .replace(/<br\s*\/?>/g, '')
      .replace(/<\/?[^>]+(>|$)/g, '')
      .trim()
  if (!stripped && !cleaned.includes('<img')) return ""
  return html
}

watch(
    () => props.reviewTxt,
    (newVal, oldVal) => {
      if (newVal !== oldVal) {
        // 反显（清洗 Quill 光标占位符，避免假空内容阻塞输入）
        content.value = cleanReviewContent(newVal)
      }
    }
)

watch(
    () => props.isCacheMode,
    (newVal, oldVal) => {
      if (newVal !== oldVal) {
        // 缓存模式改变为false时清除缓存功能
        console.log("缓存模式更改")
        if (!newVal) {
          console.log("缓存模式关闭，清空缓存计时器")
          clearCacheInterval()
        } else {
          onEditorFocus()
        }
      }
    }
)

// 判断富文本内容是否为空（仅含空标签如 <p><br></p>）
const isContentEmpty = (html) => {
  if (!html) return true
  // 先移除 Quill 光标占位符
  const cleaned = html.replace(/<span class="ql-cursor">.*?<\/span>/g, '')
  const stripped = cleaned
      .replace(/<br\s*\/?>/g, '')
      .replace(/<\/?[^>]+(>|$)/g, '')
      .replace(/&nbsp;/g, '')
      .replace(/﻿/g, '')
      .trim()
  return !stripped && !cleaned.includes('<img')
}

// 获取清洗后的富文本内容（空 HTML 返回空字符串）
const getTxt = () => {
  return isContentEmpty(content.value) ? "" : content.value
}
// 获取文本内容是否过长状态
const getTxtTooLongState = () => {
  return isTxtTooLong.value
}
// 删除富文本缓存
const removeCache = (id) => {
  if (itemDescStore().itemDesc.cacheId === id) {
    itemDescStore().removeItemDesc()
  }
}

/*编辑器事件*/
const isTimer = ref(false)
const cacheInterval = ref(null)
const onEditorBlur = (quill) => {
  console.log('编辑器失去焦点', itemDescStore().itemDesc)
}

const onEditorFocus = (quill) => {
  console.log('编辑器获得焦点', quill)
  if (props.isCacheMode) {
    if (!isTimer.value) {
      console.log("缓存模式开始")
      isTimer.value = true
      // 5分钟缓存一次内容
      cacheInterval.value = setInterval(() => {
        let cache = {
          cacheId: props.cacheId,
          content: content,
          ttl: Date.now()
        }
        itemDescStore().setItemDesc(cache)
        ElMessage({
          message: `${props.title}缓存成功`,
          type: 'success',
        })
      }, 5 * 60 * 1000)
    }
  }
}

const onEditorReady = (quill) => {
  console.log('编辑器准备就绪', quill)
}

onUnmounted(() => {
  // 销毁时清除计时器
  console.log("run in onUnmounted")
  if (props.isCacheMode) {
    clearCacheInterval()
  }
})

// 使用缓存内容替换content
const useCache = () => {
  content.value = itemDescStore().itemDesc.content
}

// 获取富文本中所有图片地址，储存为数组
const getImagesUrlArr = () => {
  const quill = quillEditor.value.getQuill()
  const images = quill.root.querySelectorAll('img');
  console.log("images", images)
  let imgArr = []
  images.forEach(image => {
    imgArr.push(image.src)
  })
  return imgArr
}

// 清空缓存计时器
const clearCacheInterval = () => {
  clearInterval(cacheInterval.value)
  isTimer.value = false
}

// 获取富文本显示的内容长度
const getContentLength = () => {
  return contentLength.value
}

// 校验内容是否为空
const isEmpty = () => {
  return contentLength.value === 0 && getImagesUrlArr().length === 0
}

defineExpose({
  getTxt,
  getContentLength,
  getTxtTooLongState,
  removeCache,
  getImagesUrlArr,
  isEmpty
})
</script>

<style scoped>
#YTEditor_container {
  background-color:#fff;
  position: relative;
  width: 100%;
}
/*汉化*/
/*标题部分*/
::v-deep(.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="1"]::before),
::v-deep(.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="1"]::before) {
  content: "标题1";
}
::v-deep(.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="2"]::before),
::v-deep(.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="2"]::before) {
  content: "标题2";
}
::v-deep(.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="3"]::before),
::v-deep(.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="3"]::before) {
  content: "标题3";
}
::v-deep(.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="4"]::before),
::v-deep(.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="4"]::before) {
  content: "标题4";
}
::v-deep(.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="5"]::before),
::v-deep(.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="5"]::before) {
  content: "标题5";
}
::v-deep(.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="6"]::before),
::v-deep(.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="6"]::before) {
  content: "标题6";
}
::v-deep(.ql-snow .ql-picker.ql-header .ql-picker-item::before),
::v-deep(.ql-snow .ql-picker.ql-header .ql-picker-label::before) {
  content: "正文";
}
/*字体大小部分*/
::v-deep(.ql-snow .ql-picker.ql-size .ql-picker-item[data-value=small]::before),
::v-deep(.ql-snow .ql-picker.ql-size .ql-picker-label[data-value=small]::before) {
  content: "10px";
}
::v-deep(.ql-snow .ql-picker.ql-size .ql-picker-label::before),
::v-deep(.ql-snow .ql-picker.ql-size .ql-picker-item::before) {
  content: "16px";
}
::v-deep(.ql-snow .ql-picker.ql-size .ql-picker-label[data-value=large]::before),
::v-deep(.ql-snow .ql-picker.ql-size .ql-picker-item[data-value=large]::before) {
  content: "18px";
}
::v-deep(.ql-snow .ql-picker.ql-size .ql-picker-label[data-value=huge]::before),
::v-deep(.ql-snow .ql-picker.ql-size .ql-picker-item[data-value=huge]::before) {
  content: "32px";
}
::v-deep(.ql-editor) {
  min-height: 470px;
  max-height: 800px;
  overflow: auto;
  padding-bottom: 30px;
}
.editorCount {
  background: var(--el-fill-color-blank);
  bottom: 5px;
  color: var(--el-color-info);
  font-size: 12px;
  line-height: 14px;
  position: absolute;
  right: 10px;
}
.editorCountErr {
  background: var(--el-fill-color-blank);
  bottom: 5px;
  color: var(--el-color-danger);
  font-size: 12px;
  line-height: 14px;
  position: absolute;
  right: 10px;
  font-weight: bolder;
}
::v-deep(#textarea-emoji) {
  top: 0!important;
}

/* 确保下拉菜单中的字体大小正确显示 */
/*::v-deep(.ql-snow .ql-picker.ql-size .ql-picker-label::before) {
  content: attr(data-value) !important;
}
::v-deep(.ql-snow .ql-picker.ql-size .ql-picker-item::before) {
  content: attr(data-value) !important;
}*/

/* 移除默认的字体大小样式 */
/*::v-deep(.ql-snow .ql-picker.ql-size .ql-picker-label[data-value]::before) {
  content: attr(data-value) !important;
}*/
/*::v-deep(.ql-snow .ql-picker.ql-size .ql-picker-item[data-value]::before) {
  content: attr(data-value) !important;
}*/

/* 应用实际字体大小到下拉选项 */
/*::v-deep(.ql-snow .ql-picker.ql-size .ql-picker-item) {
  font-size: inherit !important;
}*/
</style>
